import React from 'react';
import { deleteInvoicePost, } from '@Api';
import { IAbilities, IAuth, IOrganizationStore, IInvoiceStore, } from '@Store';
import { CrudEnum, IPreviewInvoicePost, PermissionEnum, EnumTranslateTitle, TInvoicePostSortableListCol, } from 'atlas-shared';
import { tableColumnDatetimeSorter, tableColumnBooleanRender, tableColumnStringSorter, timeSince, } from '@Utils';
import { TFunction, } from 'i18next';
import { NavigateFunction, } from 'react-router';
import { ColumnsType, } from 'antd/lib/table/interface';
import { listAuditButton, listCloneButton, listDeleteButton, listEditButton, GetColumnSearchProps, GetColumnSearchEqualProps, } from '../components';
import { IWithListProps, } from '@Hocs/with.list';

export const InvoicePostListColumns = (t: TFunction, navigate: NavigateFunction, showDeleted: boolean, organizations: IOrganizationStore, invoices: IInvoiceStore, abilities: IAbilities, auth: IAuth, buttons: Array<(row: IPreviewInvoicePost) => React.ReactElement> = [], filters: IWithListProps<IPreviewInvoicePost, TInvoicePostSortableListCol>['filters'], sorter: IWithListProps<IPreviewInvoicePost, TInvoicePostSortableListCol>['sorter']): ColumnsType<IPreviewInvoicePost> => {
  return [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IPreviewInvoicePost, b: IPreviewInvoicePost) => a.id - b.id,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'id')?.order || null,
      filteredValue: filters.id || null,
      ...GetColumnSearchEqualProps(t, 'id', t('ID')),
      className: 'table-cell-id',
    },
    auth.user.__admin_organization_ids.length > 1 && {
      title: t('ORGANIZATION'),
      dataIndex: 'organization_id',
      key: 'organization_id',
      render: (organization_id: IPreviewInvoicePost['organization_id'], invoice_post: IPreviewInvoicePost) => organizations.dict?.[organization_id]?.title || t('ORGANIZATION_NOT_FOUND', { replace: { organization_id, }, }),
      sorter: (a: IPreviewInvoicePost, b: IPreviewInvoicePost) => (organizations.dict?.[a.organization_id]?.title || '').localeCompare(organizations.dict?.[b.organization_id]?.title || ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'organization_id')?.order || null,
      filteredValue: filters.organization_id || null,
      ...GetColumnSearchProps(t, 'organization_id', t('ORGANIZATION'), organizations.organizations),
      className: 'table-cell-organization_id',
    },
    {
      title: t('INVOICE'),
      dataIndex: 'invoice_id',
      key: 'invoice_id',
      render: (invoice_id: IPreviewInvoicePost['invoice_id'], invoice_post: IPreviewInvoicePost) => invoices.dict?.[invoice_id]?.title || t('INVOICE_NOT_FOUND', { replace: { invoice_id, }, }),
      sorter: (a: IPreviewInvoicePost, b: IPreviewInvoicePost) => (invoices.dict?.[a.invoice_id]?.title || '').localeCompare(invoices.dict?.[b.invoice_id]?.title || ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'invoice_id')?.order || null,
      filteredValue: filters.invoice_id || null,
      ...GetColumnSearchProps(t, 'invoice_id', t('INVOICE'), invoices.invoices),
      className: 'table-cell-invoice_id',
    },
    {
      title: t('CURRENCY'),
      dataIndex: 'currency',
      key: 'currency',
      render: (currency: IPreviewInvoicePost['currency'], invoice_post: IPreviewInvoicePost) => EnumTranslateTitle('INVOICE_CURRENCY', currency, t),
      sorter: (a: IPreviewInvoicePost, b: IPreviewInvoicePost) => EnumTranslateTitle('INVOICE_CURRENCY', a.currency, t).localeCompare(EnumTranslateTitle('INVOICE_CURRENCY', b.currency, t)),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'currency')?.order || null,
      className: 'table-cell-currency',
    },
    {
      title: t('TITLE'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a: IPreviewInvoicePost, b: IPreviewInvoicePost) => tableColumnStringSorter(a.title, b.title),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'title')?.order || null,
      className: 'table-cell-title',
    },
    {
      title: t('AMOUNT'),
      dataIndex: 'amount',
      key: 'amount',
      className: 'table-cell-amount',
    },
    {
      title: t('EXCLUDE'),
      dataIndex: 'exclude',
      key: 'exclude',
      render: (exclude: IPreviewInvoicePost['exclude'], invoice_post: IPreviewInvoicePost) => tableColumnBooleanRender(t, exclude),
      sorter: (a: IPreviewInvoicePost, b: IPreviewInvoicePost) => (a.exclude ? 1 : 0) > (b.exclude ? 1 : 0) ? 1 : -1,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'exclude')?.order || null,
      className: 'table-cell-exclude',
    },
    {
      title: t('CREATED_AT'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: IPreviewInvoicePost['created_at'], invoice_post: IPreviewInvoicePost) => <div className='date-column'>{created_at ? timeSince(t, new Date(created_at), true) : ''}</div>,
      sorter: (a: IPreviewInvoicePost, b: IPreviewInvoicePost) => tableColumnDatetimeSorter(a.created_at, b.created_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'created_at')?.order || null,
      className: 'table-cell-created_at',
    },
    
    {
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      render: (_: any, row: IPreviewInvoicePost) => (
        <div className={'table-operation-wrp'}>
          {buttons.map(button => button(row))}
          {abilities.can(row.organization_id, CrudEnum.Insert, PermissionEnum.InvoicePost, row) && listCloneButton(t, navigate, 'invoice_post', row)}
          {abilities.can(row.organization_id, CrudEnum.Read, PermissionEnum.Audit, row) && listAuditButton(t, navigate, 'invoice_post', row)}
          {abilities.can(row.organization_id, CrudEnum.Update, PermissionEnum.InvoicePost, row) && listEditButton(t, navigate, 'invoice_post', row)}
          {abilities.can(row.organization_id, CrudEnum.Delete, PermissionEnum.InvoicePost, row) && listDeleteButton(t, row, deleteInvoicePost)}
        </div>
      ),
    },
  ].filter(Boolean) as ColumnsType<IPreviewInvoicePost>;

};
